import React, { ChangeEvent } from 'react'
import { Box, MenuItem, Switch, TextField, Tooltip } from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import { useReactiveVar } from '@apollo/client'
import { SingleSettingSection } from '../../common/SingleSettingSection'
import { EnergyBusFragment, HourSeriesType } from '../../../generated/graphql'
import { projectVar } from '../../../apollo/reactive-vars'
import { useEnergyBus } from '../../../energy-bus/use-energy-bus'

type Props = {
  energyBus: EnergyBusFragment
}

const EMPTY_VALUE = 'empty'

export function EnergyBusEnergySystemTab({
  energyBus,
}: Props): React.ReactElement {
  const { t } = useTranslation()
  const project = useReactiveVar(projectVar)

  const { updateEnergyBus } = useEnergyBus()

  const handleChange = async (
    event: ChangeEvent<{ name?: string | undefined; value: unknown }>,
  ) => {
    const value =
      event.target.value === EMPTY_VALUE ? null : (event.target.value as string)

    await updateEnergyBus(energyBus.id, {
      wasteHourSeriesId: value,
    })
  }

  const wasteOptions =
    project?.hourSeriesCollections
      .flatMap((collection) => collection.hourSeries)
      .filter((hourSeries) => hourSeries.type === HourSeriesType.WasteHeating)
      .map(({ id, name }) => ({ value: id, label: name })) ?? []

  wasteOptions.unshift({ value: EMPTY_VALUE, label: '-' })

  const wasteHourSeriesValue = energyBus.wasteHourSeries?.id ?? EMPTY_VALUE

  return (
    <Box>
      <SingleSettingSection
        title={t('energyBus.tabs.energySystem.connectedToGroundSourceField')}
      >
        <Box height="100%" display="flex" alignItems="center">
          <Tooltip
            title={
              t(
                'energyBus.tabs.energySystem.connectedToGroundSourceFieldTooltip',
              ) ?? ''
            }
            arrow
          >
            <span>
              <Switch
                name="connectedToGroundSourceField"
                color="primary"
                checked
                disabled
              />
            </span>
          </Tooltip>
        </Box>
      </SingleSettingSection>

      <SingleSettingSection
        title={t('energyBus.tabs.energySystem.wasteHeatSource')}
      >
        <Box height="100%" display="flex" alignItems="center">
          <TextField
            value={wasteHourSeriesValue}
            onChange={handleChange}
            select
          >
            {wasteOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      </SingleSettingSection>
    </Box>
  )
}
